import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { superAdminApi } from '../../services/api';
import toast from 'react-hot-toast';

const StatCard = ({ icon, label, value, sub, color }) => (
  <div style={{
    background: '#fff', borderRadius: 14, padding: '22px 24px',
    border: '1px solid #ede9fe', display: 'flex', alignItems: 'flex-start', gap: 16,
  }}>
    <div style={{
      width: 48, height: 48, borderRadius: 12, fontSize: 22,
      background: color || '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>{icon}</div>
    <div>
      <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: '#1f2937', lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>{sub}</div>}
    </div>
  </div>
);

const SuperAdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    superAdminApi.getDashboard()
      .then(res => setData(res.data.data))
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));

    superAdminApi.getActivityLogs({ limit: 20 })
      .then(res => setLogs(res.data.data || []))
      .catch((err) => { if (err?.response?.status !== 404) toast.error('Failed to load activity logs'); setLogs([]); })
      .finally(() => setLogsLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
      <div style={{ width: 36, height: 36, border: '3px solid #ede9fe', borderTopColor: '#7c3aed', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1f2937', marginBottom: 6 }}>Platform Dashboard</h1>
      <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 28 }}>Overview of all restaurants on the platform</p>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 32 }}>
        <StatCard icon="🏪" label="Active Restaurants" value={data?.stats?.totalRestaurants ?? 0} color="#f5f3ff" />
        <StatCard icon="📦" label="Total Orders" value={data?.stats?.totalOrders?.toLocaleString() ?? 0} color="#fef3c7" />
        <StatCard icon="💰" label="Platform Revenue" value={`₹${(data?.stats?.totalRevenue ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`} color="#dcfce7" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Top restaurants by revenue */}
        <div style={{ background: '#fff', borderRadius: 14, padding: 24, border: '1px solid #ede9fe' }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', marginBottom: 16 }}>🏆 Top Restaurants (30 days)</h2>
          {data?.topRestaurants?.length === 0 && (
            <p style={{ color: '#9ca3af', fontSize: 13 }}>No data yet.</p>
          )}
          {data?.topRestaurants?.map((r, i) => (
            <div
              key={r.id}
              onClick={() => navigate(`/super-admin/restaurants/${r.id}`)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                background: i % 2 === 0 ? '#fafafa' : '#fff',
                marginBottom: 4,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 18, width: 24, textAlign: 'center' }}>
                  {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`}
                </span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#1f2937' }}>{r.name}</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>/{r.slug}</div>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#7c3aed' }}>
                  ₹{parseFloat(r.revenue).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>{r.orders} orders</div>
              </div>
            </div>
          ))}
        </div>

        {/* Recent restaurants */}
        <div style={{ background: '#fff', borderRadius: 14, padding: 24, border: '1px solid #ede9fe' }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', marginBottom: 16 }}>🆕 Recently Added</h2>
          {data?.recentRestaurants?.map(r => (
            <div
              key={r.id}
              onClick={() => navigate(`/super-admin/restaurants/${r.id}`)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                marginBottom: 4, transition: 'background 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f5f3ff'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#1f2937' }}>{r.name}</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>/{r.slug} · {r.plan}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  fontSize: 11, padding: '3px 8px', borderRadius: 99,
                  background: r.is_active ? '#dcfce7' : '#fee2e2',
                  color: r.is_active ? '#166534' : '#991b1b', fontWeight: 600,
                }}>
                  {r.is_active ? 'Active' : 'Disabled'}
                </span>
              </div>
            </div>
          ))}
          <button
            onClick={() => navigate('/super-admin/restaurants')}
            style={{
              marginTop: 12, width: '100%', padding: '10px', background: 'transparent',
              border: '1px dashed #d8b4fe', borderRadius: 8, color: '#7c3aed',
              fontSize: 13, fontWeight: 500, cursor: 'pointer',
            }}
          >
            View All Restaurants →
          </button>
        </div>
      </div>

      {/* Activity Log */}
      <div style={{ marginTop: 24, background: '#fff', borderRadius: 14, padding: 24, border: '1px solid #ede9fe' }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', marginBottom: 16 }}>🔔 Activity Log</h2>
        {logsLoading ? (
          <p style={{ color: '#9ca3af', fontSize: 13 }}>Loading...</p>
        ) : logs.length === 0 ? (
          <p style={{ color: '#9ca3af', fontSize: 13 }}>Koi activity nahi mili abhi tak.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {logs.map((log, i) => (
              <div key={log.id} style={{
                display: 'flex', alignItems: 'flex-start', gap: 14,
                padding: '12px 0',
                borderBottom: i < logs.length - 1 ? '1px solid #f3f4f6' : 'none',
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                  background: log.action === 'password_changed' ? '#fef3c7' : '#f5f3ff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
                }}>
                  {log.action === 'password_changed' ? '🔑' : '📋'}
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontSize: 13, color: '#1f2937', fontWeight: 500 }}>{log.details?.replace(/undefined/g, "?") || "Activity recorded"}</p>
                  <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                    <span style={{ fontSize: 11, color: '#7c3aed', fontWeight: 600 }}>🏪 {log.restaurant_name}</span>
                    <span style={{ fontSize: 11, color: '#9ca3af' }}>
                      {new Date(log.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default SuperAdminDashboard;
